import PropTypes from "prop-types"
import { createRef, useState, useEffect } from "react"
import { connect } from "react-redux"

import { fetchSize } from "areas/product/jewlr/modals/helper"
import SizeGuideHalfModal from "areas/product/jewlr/modals/size_guide_half_modal"
import {
  SizeBox,
  SizeContainer,
  Size,
} from "areas/product/jewlr/modals/styled-components"
import { updateChoice } from "areas/product/store/actions"
import {
  GradientContainer,
  OptionPrice,
  OptionsSlider,
} from "areas/product/wizard-options/components"
import { openModal } from "components/modal"
import Button from "components/ui/button"
import Media from "components/ui/media"
import Select from "components/ui/select"
import { isTestMode } from "helpers/application"
import { to_currency } from "helpers/number"
import { filterProductOptions } from "helpers/product/product-filtering"
import { cn } from "styles/utils"

const DescriptionText = (props) => (
  <p className="text-12 font-light" {...props} />
)

const InternalSizeOptions = ({
  data,
  errorText,
  options,
  productType,
  selectedValue,
  setChoice,
  sizePref,
  isOpen,
  modal = false,
}) => {
  var miniScrollerRef = createRef()
  var scrollToRef = createRef()
  var selectedSizeRef = createRef()

  const [shouldScroll, setShouldScroll] = useState(false)
  const [selectedSize, setSelectedSize] = useState(null)
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    if (selectedValue && !selectedSize) {
      options.map((sz) => {
        if (sz.value === selectedValue) {
          setSelectedSize(sz)
        }
      })
    }
    if (!shouldScroll && isOpen && !scrolled) {
      setShouldScroll(true)
    } else if (shouldScroll && isOpen) {
      setShouldScroll(false)
    } else if (!isOpen) {
      setScrolled(false)
      setShouldScroll(false)
    }
  }, [selectedSize, isOpen, scrolled, selectedValue])

  const scrollToSelected = () => {
    if (!miniScrollerRef || !shouldScroll) {
      return
    }
    if (!selectedValue) {
      miniScrollerRef.scrollLeft =
        scrollToRef.offsetLeft -
        (window.innerWidth - scrollToRef.offsetWidth) / 2
    } else {
      miniScrollerRef.scrollLeft =
        selectedSizeRef.offsetLeft -
        (window.innerWidth - scrollToRef.offsetWidth) / 2
    }
    setScrolled(true)
  }

  const sizeTipText = data.not_a_ring ? null : (
    <div className="tablet:py-20 tablet:text-left text-center">
      <DescriptionText>
        <strong>
          The average ring size for {data.male ? "men" : "women"} is{" "}
          {data.avg_size}
        </strong>
      </DescriptionText>
      <DescriptionText>
        Unsure of the size? Please see our{" "}
        <Button
          className="text-primary"
          onClick={() => {
            openModal("#size-chart")
            if (!isTestMode) {
              dataLayer.push({
                event: "viewed-ring-size-guide",
              })
            }
          }}
          text
        >
          Size Chart
        </Button>
        .
      </DescriptionText>
      {data.size_tip && (
        <DescriptionText dangerouslySetInnerHTML={{ __html: data.size_tip }} />
      )}
    </div>
  )

  const mobileContent = (
    <div className="flex justify-center">
      <OptionsSlider
        aria-label={`${data.heading.title} Options`}
        className={cn(
          "tablet:grid grid-cols-6",
          modal ? "tablet:mx-auto -mx-24" : "p-10"
        )}
        data-slider="true"
        ref={(r) => {
          miniScrollerRef = r
          scrollToSelected()
        }}
        role="radiogroup"
      >
        {options.map((sz, index) => (
          <SizeContainer
            data-index={index}
            data-price={sz.price}
            data-scroll-to={
              (sz.value == "7.00" && sizePref && sizePref.forHer) ||
              (sz.value == "10.00" && sizePref && sizePref.forHim)
            }
            data-sia={sz.sia}
            data-title={sz.value}
            data-value={sz.value}
            key={`li-sliderSizeOption-${sz.value}`}
            ref={(r) => {
              if (selectedValue && sz.value === selectedValue) {
                selectedSizeRef = r
              } else if (sz.value == data.avg_size) {
                scrollToRef = r
              }
            }}
          >
            <SizeBox
              aria-checked={!!selectedValue && selectedValue === sz.value}
              availableHoliday={sz.hasInventory}
              className={cn(
                "mb-12",
                productType === "RING" ? "text-20" : "text-14"
              )}
              data-cy={`product-wizard-option-${data.pref}-${sz.value}`}
              onClick={(e) => {
                setChoice(data.pref, sz, e)
                setSelectedSize(sz)
              }}
              role="radio"
            >
              <Size
                dangerouslySetInnerHTML={{
                  __html: fetchSize(sz.option_description),
                }}
              />
            </SizeBox>
            <OptionPrice>
              {sz.price > 0 ? (
                "(+" + to_currency(sz.price) + ")"
              ) : (
                <span>&nbsp;</span>
              )}
            </OptionPrice>
          </SizeContainer>
        ))}
      </OptionsSlider>
    </div>
  )

  const desktopContent = (
    <Media greaterThanOrEqual="tablet">
      <Select
        className="border-grey-65 w-3/4"
        data-pref="sz"
        onChange={(e) => {
          let found = false
          options.map((sz) => {
            if (sz.value === e.target.value) {
              found = true
              setSelectedSize(sz)
              setChoice(data.pref, sz, e)
            }
          })
          if (!found) {
            setChoice(data.pref, { value: "" }, e)
          }
        }}
        value={selectedValue}
      >
        <option value="">Please select your size</option>
        {options.map((sz, index) => (
          <option key={index} value={sz.value}>
            {sz.title} {sz.price > 0 ? to_currency(sz.price) : ""}{" "}
            {sz.available}
          </option>
        ))}
      </Select>
    </Media>
  )
  if (modal) {
    return (
      <GradientContainer className="px-0" hideSlideShadow>
        {mobileContent}
      </GradientContainer>
    )
  }
  return (
    <GradientContainer className="mb-16">
      {errorText && (
        <div className="mb-8">
          <p className="text-12">{errorText}</p>
        </div>
      )}
      {desktopContent}
      <Media lessThan="tablet">{mobileContent}</Media>
      {sizeTipText}
      <SizeGuideHalfModal />
    </GradientContainer>
  )
}

const getSelectedMetalFilter = (state) => {
  if (
    typeof state.choices.sku === "undefined" &&
    typeof state.choices.m2 === "undefined"
  ) {
    return ""
  } else {
    // If m2 is defined, assume that is the metal filter trigger
    return {
      pref: typeof state.choices.m2 === "undefined" ? "sku" : "m2",
      value:
        typeof state.choices.m2 === "undefined"
          ? state.choices.sku.value
          : state.choices.m2.value,
    }
  }
}

const getSelectedValue = (state, ownProps) => {
  if (typeof state.choices[ownProps.pref] === "undefined") {
    return ""
  } else {
    return state.choices[ownProps.pref].value
  }
}

const getData = (state, ownProps) => {
  return state.wizard.data[ownProps.pref]
}

const mapStateToProps = (state, ownProps) => {
  let options = []
  if (state.wizard.data[ownProps.pref]) {
    options = state.wizard.data[ownProps.pref].options
  }
  if (
    ownProps.pref == "ch" ||
    ownProps.pref == "sz" ||
    /^o[0-9]+/.test(ownProps.pref) ||
    /^ch[0-9]+/.test(ownProps.pref)
  ) {
    const selectedMetalFilter = getSelectedMetalFilter(state, ownProps)
    options = filterProductOptions(options, selectedMetalFilter, true)
  }

  // Holiday - show available based on inventory
  if (
    sl.config.show_guaranteed &&
    ownProps.modal &&
    state.wizard.inventory &&
    !state.wizard.inventory.non_inventory_available_holiday_delivery
  ) {
    options = options.map((sizeOption) => {
      // Get inventory that has matching size, matching choice values, and selected metal is available for holiday delivery
      let hasInventory = state.wizard.inventory.available_inventories.some(
        (inventory) =>
          sizeOption.value === inventory.item_attributes_hash.sz &&
          Object.keys(inventory.item_attributes_hash)
            .filter((a) => a !== "sz")
            .some((a) => {
              // Check inventory attributes matches choice values
              let attributeValue = inventory.item_attributes_hash[a]
              if (a === "as") {
                attributeValue = attributeValue === "CZ" ? "04CZIR" : "04DIAM"
              }
              return state.choices[a].value === attributeValue
            }) &&
          state.choices.sku &&
          inventory.sku_availability_hash[state.choices.sku.value]
            .available_holiday
      )

      return { ...sizeOption, hasInventory }
    })
  }

  return {
    data: getData(state, ownProps),
    errorText:
      state.choices[ownProps.pref] && state.choices[ownProps.pref].error,
    isOpen:
      state.panel.openedPanelIndexes.indexOf("sz") > -1 || ownProps.opened,
    options,
    productType: state.wizard.description.product_type,
    selectedValue: getSelectedValue(state, ownProps),
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    setChoice: (pref, data) => {
      dispatch(updateChoice(pref, data, true))
      try {
        if (typeof ownProps.callback !== "undefined" && ownProps.callback) {
          ownProps.callback()
        }
      } catch (error) {
        console.log("CALLBACK ERROR:", error)
      }
    },
  }
}

InternalSizeOptions.propTypes = {
  data: PropTypes.object,
  errorText: PropTypes.string,
  isOpen: PropTypes.bool,
  modal: PropTypes.bool,
  options: PropTypes.array,
  productType: PropTypes.string,
  selectedValue: PropTypes.string,
  setChoice: PropTypes.func,
  sizePref: PropTypes.object,
}

const SizeOptions = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalSizeOptions)

export default SizeOptions
